import { type VariantProps, cva } from 'class-variance-authority';
import type { LucideIcon } from 'lucide-react';
import type { ComponentProps, MouseEvent } from 'react';

import { cn } from '../lib/cn';
import { Icon } from './icon';
import { Spinner } from './spinner';

/** 눌렸다는 확인 — 3%·120ms (D3 §4.5 · D4 §4). 글자 버튼(`link`)에는 주지 않는다. */
const PRESS = 'motion-safe:active:scale-97';

/**
 * 채움 버튼의 비활성 — 색을 뺀다. 투명도로 흐리면 네이비가 연파랑이 돼 활성처럼 보였다(D150).
 * 화면당 채움 버튼은 하나다(D3 §4.1) — 비활성이어도 그 자리를 지킨다.
 */
const FILLED_DISABLED = 'disabled:bg-disabled disabled:text-disabled-fg';

export const buttonVariants = cva(
  cn(
    // D1 §5: 버튼·입력은 8px(rounded-lg)이 기본 단위다. 카드(12px)와 구분한다.
    // [D4-05] font-bold → font-medium. 버튼은 눌리는 것이지 외치는 것이 아니다.
    // 강조는 배경색이 이미 하고 있다.
    'gap-snug relative inline-flex items-center justify-center rounded-lg font-medium whitespace-nowrap',
    'ease-flow duration-instant transition-[color,background-color,border-color,scale]',
    'disabled:pointer-events-none',
    'aria-busy:pointer-events-none',
  ),
  {
    variants: {
      variant: {
        /** 앱 내부의 주요 액션. D1 §3 — 액션은 네이비가 담당한다. */
        primary: cn(
          'bg-action hover:bg-action-hover active:bg-action-active text-action-fg',
          FILLED_DISABLED,
          PRESS,
        ),
        outline: cn(
          'border-line bg-surface text-ink hover:bg-highlight disabled:text-disabled-fg border',
          PRESS,
        ),
        ghost: cn(
          'text-ink-muted hover:bg-highlight hover:text-ink disabled:text-disabled-fg',
          PRESS,
        ),
        danger: cn(
          'bg-danger hover:bg-danger-hover active:bg-danger-active text-white',
          FILLED_DISABLED,
          PRESS,
        ),
        link: 'text-link hover:text-link-hover disabled:text-disabled-fg underline-offset-4 hover:underline',
        /**
         * 오렌지 CTA. **랜딩 진입 CTA 전용** — 제품 전체에서 한 곳뿐이다.
         * 앱 내부의 "중요한 버튼"에 쓰지 않는다. D1 §3 "오렌지의 자리" 참조.
         *
         * accent-700(5.06:1)이다. 크기·굵기에 상관없이 AA를 통과하므로
         * 이전처럼 "lg + 800에서만 써야 한다"는 조건이 붙지 않는다 — D4-05 주석 참조.
         */
        cta: cn(
          'bg-cta hover:bg-cta-hover active:bg-cta-active text-cta-fg font-semibold',
          FILLED_DISABLED,
          PRESS,
        ),
      },
      size: {
        sm: 'px-row h-8 text-xs',
        md: 'px-card h-10 text-sm',
        lg: 'px-panel h-12 text-lg',
        /** 아이콘 전용. 반드시 `aria-label`을 함께 준다 — L2-06 §8 접근성 */
        icon: 'size-10 p-0',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps extends ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  /** 라벨 앞 아이콘. `pending`이면 이 자리를 스피너가 대신해 버튼 너비가 바뀌지 않는다. */
  icon?: LucideIcon;
  /**
   * 누른 뒤 응답을 기다리는 중. 다시 누를 수 없고(`aria-disabled`) `aria-busy`로 알린다.
   * `disabled`와 달리 초점을 잃지 않는다.
   */
  pending?: boolean;
}

const SPINNER_FADE_IN = 'ease-flow duration-fast transition-opacity starting:opacity-0';

export function Button({
  className,
  variant,
  size,
  type = 'button',
  icon,
  pending = false,
  onClick,
  children,
  ...props
}: ButtonProps) {
  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    if (pending) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  }

  const spinner = <Spinner size="sm" decorative className={SPINNER_FADE_IN} />;
  const leading = icon ? pending ? spinner : <Icon as={icon} size="sm" /> : null;
  const centered = pending && !icon;

  return (
    <button
      type={type}
      aria-busy={pending || undefined}
      aria-disabled={pending || undefined}
      className={cn(buttonVariants({ variant, size }), className)}
      onClick={handleClick}
      {...props}
    >
      {leading}
      {centered ? (
        /* 라벨은 접근 이름으로 남기고 보이지만 않게 둔다 — 너비가 그대로다. */
        <span className="gap-snug inline-flex items-center opacity-0">{children}</span>
      ) : (
        children
      )}
      {centered ? (
        <span className="absolute inset-0 flex items-center justify-center">{spinner}</span>
      ) : null}
    </button>
  );
}
